import { LuInfo, LuX } from 'react-icons/lu'

import { Button } from '@/lib/core/components/Button'
import { FieldError, FieldErrorMessage } from '@/lib/core/components/TextField/TextField.styles'
import { Tooltip, TooltipContainerProvider } from '@/lib/core/components/Tooltip'

import { LocationSearch } from '../LocationSearch/LocationSearch'
import styles from '../TaskCellPopout.module.css'
import { TaskLocationPopoutProps } from './TaskLocationPopout.types'
import { getLocationLabel, isLineString } from './TaskLocationPopout.utils'
import { useTaskLocationPopout } from './useTaskLocationPopout'

export const TaskLocationPopout = ({
  task,
  testId,
  locationInput,
  onUpdated,
  mode,
  placeholder,
  error,
}: TaskLocationPopoutProps) => {
  const {
    taskDefinition,
    location,
    searchedLocations,
    isDisabled,
    buttonRef,
    dialogRef,
    searchInputRef,
    showPopover,
    handleSearchUpdated,
    handleSearchedResults,
    handleUpdateLocations,
  } = useTaskLocationPopout({ task, locationInput, onUpdated, mode })

  const computedTestId = testId ?? `task-location-popout-${taskDefinition.id}`

  return (
    <div
      data-testid={`${computedTestId}-container`}
      className={mode === 'CREATE' ? '' : styles.locationContainer}
    >
      <div className={`${styles.taskCellPopout}`.trim()} data-testid={computedTestId}>
        <Button
          id={`assigned-location-dialog-${taskDefinition.id}`}
          data-testid={`${computedTestId}-location-button`}
          ref={buttonRef}
          variant="ghost"
          className={`${mode === 'CREATE' ? styles.locationButtonCreationMode : styles.locationButton} ${error ? styles.locationButtonError : ''}`.trim()}
          onClick={showPopover}
          disabled={isDisabled}
        >
          <div className={styles.taskCellPopoutButtonLabel} title={location?.name ?? ''}>
            {location?.name ?? placeholder ?? '-'}
          </div>
        </Button>

        <dialog
          id={`assigned-task-location-dialog-${taskDefinition.id}`}
          data-testid={`${computedTestId}-location-dialog`}
          ref={dialogRef}
          className={styles.popoutLocationContainer}
          onClick={() => dialogRef.current?.close()}
          popover="auto"
        >
          <TooltipContainerProvider container={dialogRef.current}>
            <div className={styles.popoutWrapper}>
              <div className={styles.popoutHeader}>
                <div className={styles.searchContainer}>
                  <LocationSearch
                    ref={searchInputRef}
                    onSearchUpdated={handleSearchUpdated}
                    onSearchedResults={handleSearchedResults}
                  />
                </div>
                <div className={styles.headerActions}>
                  <button
                    className={styles.actionButton}
                    onClick={(e) => {
                      e.stopPropagation()
                      dialogRef.current?.hidePopover()
                    }}
                  >
                    <LuX size={16} />
                  </button>
                </div>
              </div>

              <div className={styles.optionsWrapper}>
                {searchedLocations.map((loc) => {
                  const label = getLocationLabel(loc)

                  if (isLineString(loc)) {
                    return (
                      <div key={`location-option-${loc.id}`} className={styles.disabledOption}>
                        <span className={styles.disabledOptionContent}>{label}</span>
                        <div className={styles.disabledOptionIcon}>
                          <Tooltip
                            trigger="mouseenter"
                            placement="right"
                            tooltipContent={
                              <div className={styles.disabledOptionTooltip}>
                                LineStrings cannot be selected as a task location
                              </div>
                            }
                          >
                            <span>
                              <LuInfo size="16" strokeWidth={1.6} />
                            </span>
                          </Tooltip>
                        </div>
                      </div>
                    )
                  }

                  return (
                    <div
                      key={`location-option-${loc.id}`}
                      onClick={() => handleUpdateLocations(loc)}
                      className={styles.option}
                    >
                      {label}
                    </div>
                  )
                })}
              </div>
            </div>
          </TooltipContainerProvider>
        </dialog>
      </div>
      {error && (
        <FieldError>
          <FieldErrorMessage data-testid={`${computedTestId}-error`}>{error}</FieldErrorMessage>
        </FieldError>
      )}
    </div>
  )
}
